import { html, TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import type { Article, CtaType } from '../article-cards.interface';
import { buildImageElement, buildHeading } from '../templates/article-cards.shared';
import type { CardRenderer, RendererConfig } from './article-cards.renderer';

/**
 * Renderer for the 'cards' and 'manual' display formats.
 */
export class CardsRenderer implements CardRenderer {
  private readonly showImage: boolean;
  private readonly showDescription: boolean;
  private readonly showTags: boolean;
  private readonly ctaType: CtaType;

  constructor(private readonly config: RendererConfig, private readonly isManual: boolean) {
    this.showImage = isManual || config.showImage;
    this.showDescription = isManual || config.showDescription;
    this.showTags = isManual || config.showTags;
    this.ctaType = isManual ? 'button' : config.ctaType;
  }

  renderCard(article: Article, index: number, total?: number): TemplateResult {
    const { listId, headingLevel, imageSize, imagePosition, onCardClick, onCtaClick } = this.config;
    const { showImage, showDescription, showTags, ctaType } = this;
    const { path, title, date, desc, tags, imageUrl, imageAlt } = article;
    const fullPath = `${path}.html`;
    const titleId = `${listId}-card-${index}`;
    const posId = total === undefined ? undefined : `${titleId}-pos`;

    return html`
      <li class="col" aria-posinset="${index + 1}" aria-setsize="${ifDefined(total)}">
        <article
          class="card h-100 overflow-hidden rounded align-items-center text-left shadow linkedCard"
          tabindex="-1"
          @click="${onCardClick(article)}">
          ${posId === undefined
            ? ''
            : html`<span id="${posId}" class="visually-hidden">${`Item ${index + 1} of ${total}.`}</span>`}
          ${showImage
            ? html`
                <div class="bg-light d-flex align-self-stretch align-items-center p-0 overflow-hidden ratio ratio-16x9">
                  ${buildImageElement(imageUrl, imageAlt, imageSize, imagePosition)}
                </div>
              `
            : ''}

          <div class="col p-0 align-self-stretch">
            <div class="card-body d-flex flex-column h-100 p-4">
              ${ctaType !== 'button' && ctaType !== 'link'
                ? html`<a href="${fullPath}" class="card-link text-dark" aria-describedby="${ifDefined(posId)}"
                    >${buildHeading(headingLevel, 'fs-5 fw-bold', title, titleId)}</a
                  >`
                : buildHeading(headingLevel, 'fs-5 fw-bold', title, titleId)}
              <time datetime="${date}" class="d-inline-block mb-2">
                <span>${date}</span>
              </time>
              ${showDescription ? html`<p class="mb-4">${desc}</p>` : ''}
              ${ctaType === 'button'
                ? html`
                    <div class="mb-2">
                      <a
                        href="${fullPath}"
                        class="btn btn-primary"
                        aria-label="Read more about ${title}"
                        aria-describedby="${ifDefined(posId)}"
                        @click="${onCtaClick(article)}">
                        Read More
                      </a>
                    </div>
                  `
                : ''}
              ${ctaType === 'link'
                ? html`
                    <div class="mb-2">
                      <a href="${fullPath}" class="link-primary-underline" aria-describedby="${ifDefined(posId)}" @click="${onCtaClick(article)}"
                        >Read More <span aria-hidden="true">&gt;</span
                        ><span class="visually-hidden">about ${title}</span></a
                      >
                    </div>
                  `
                : ''}
              ${showTags && tags
                ? html`
                    <hr aria-hidden="true" role="presentation" />
                    <p class="tags fs-6 m-0">Tags: <strong>${tags}</strong></p>
                  `
                : ''}
            </div>
          </div>
        </article>
      </li>
    `;
  }

  renderSkeleton(): TemplateResult {
    const { showImage, showDescription } = this;
    return html`
      <li class="col article-skeleton">
        <div class="card h-100 overflow-hidden rounded shadow">
          ${showImage
            ? html`
                <div class="bg-light d-flex align-self-stretch align-items-center p-0 overflow-hidden ratio ratio-16x9">
                  <span class="placeholder w-100 h-100"></span>
                </div>
              `
            : ''}
          <div class="card-body d-flex flex-column p-4 placeholder-glow">
            <span class="placeholder col-10 placeholder-lg mb-3"></span>
            <span class="placeholder col-4 placeholder-sm mb-3"></span>
            ${showDescription
              ? html`
                  <span class="placeholder col-12 mb-2"></span>
                  <span class="placeholder col-12 mb-2"></span>
                  <span class="placeholder col-12 mb-2"></span>
                  <span class="placeholder col-11 mb-2"></span>
                  <span class="placeholder col-9 mb-4"></span>
                `
              : ''}
          </div>
        </div>
      </li>
    `;
  }

  getSkeletonCount(perRow: number, numRows: number): number {
    return this.isManual ? this.config.manualArticlesSize : perRow * numRows;
  }
}
